import { useParams, Link } from "react-router-dom";
import { useMemo } from "react";
import { articles, categories } from "@/mocks/forum";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import ArticleCard from "@/components/base/ArticleCard";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);

  const article = useMemo(() => articles.find((a) => a.id === articleId), [articleId]);

  const category = useMemo(() => {
    if (!article) return null;
    return categories.find((c) => c.slug === article.category);
  }, [article]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">文章不存在</h1>
            <p className="text-sm text-foreground-400 mb-6">您访问的文章可能已被删除或不存在</p>
            <Link
              to="/"
              className="inline-flex px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      "@type": "Organization",
      name: "楼凤论坛",
      url: "https://www.loufengbbs.com",
    },
    url: `https://www.loufengbbs.com/article/${article.id}`,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.loufengbbs.com/article/${article.id}`,
    },
  };

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "https://www.loufengbbs.com",
      },
      ...(category
        ? [
            {
              "@type": "ListItem",
              position: 2,
              name: category.name,
              item: `https://www.loufengbbs.com/category/${category.slug}`,
            },
          ]
        : []),
      {
        "@type": "ListItem",
        position: category ? 3 : 2,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-100">
          <Breadcrumb />
        </div>

        <article className="w-full px-4 md:px-6 py-8 bg-background-50">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1 min-w-0 max-w-[780px]">
              <header className="mb-8">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors cursor-pointer"
                    >
                      {category.name}
                    </Link>
                  )}
                  {article.isPinned && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                      置顶
                    </span>
                  )}
                  {article.isHot && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-600">
                      热门
                    </span>
                  )}
                </div>

                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>

                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-sm text-foreground-500">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-background-200">
                      <img
                        src={article.authorAvatar}
                        alt={article.author}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <span className="font-medium text-foreground-700">{article.author}</span>
                  </div>
                  <span className="hidden sm:block text-foreground-300">|</span>
                  <span>{article.date}</span>
                  <span className="hidden sm:block text-foreground-300">|</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views >= 10000
                      ? `${(article.views / 10000).toFixed(1)}万`
                      : article.views}{" "}
                    阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line"></i>
                    {article.comments} 评论
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-thumb-up-line"></i>
                    {article.likes} 点赞
                  </span>
                </div>
              </header>

              <div
                className="prose prose-base max-w-none text-foreground-800 leading-relaxed
                  prose-h2:text-xl prose-h2:font-bold prose-h2:text-foreground-950 prose-h2:mt-8 prose-h2:mb-4
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-strong:text-foreground-900
                  prose-ul:pl-5 prose-ol:pl-5
                  prose-li:text-foreground-700 prose-li:mb-1"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-8 pt-6 border-t border-background-200">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 rounded-full text-xs bg-secondary-100 text-secondary-700"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-8 flex items-center justify-between p-4 rounded-xl bg-background-100/50">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-background-200">
                    <img
                      src={article.authorAvatar}
                      alt={article.author}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{article.author}</p>
                    <p className="text-xs text-foreground-500">社区活跃用户</p>
                  </div>
                </div>
                <button className="px-4 py-2 rounded-full text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  + 关注作者
                </button>
              </div>
            </div>

            <aside className="w-full lg:w-[320px] shrink-0">
              <div className="sticky top-20 space-y-6">
                <div className="rounded-xl bg-background-50 border border-background-200/70 p-5">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-article-line text-secondary-500"></i>
                    文章信息
                  </h3>
                  <div className="flex flex-col gap-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-foreground-400">作者</span>
                      <span className="text-foreground-700 font-medium">{article.author}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-400">发布时间</span>
                      <span className="text-foreground-700">{article.date}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-400">分类</span>
                      <span className="text-foreground-700">
                        {category ? category.name : "-"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-400">阅读量</span>
                      <span className="text-foreground-700">
                        {article.views >= 10000
                          ? `${(article.views / 10000).toFixed(1)}万`
                          : article.views}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-400">互动</span>
                      <span className="text-foreground-700">
                        {article.comments} 评论 · {article.likes} 点赞
                      </span>
                    </div>
                  </div>
                </div>

                {relatedArticles.length > 0 && (
                  <div className="rounded-xl bg-background-50 border border-background-200/70 p-5">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                      <i className="ri-links-line text-secondary-500"></i>
                      相关文章
                    </h3>
                    <div className="flex flex-col gap-1">
                      {relatedArticles.map((ra) => (
                        <ArticleCard key={ra.id} article={ra} variant="compact" />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}